import { lazy, Suspense, useMemo } from "react";
import { useGlobalValue } from "../../stores/GlobalState";
import { useNavigation, ROUTES } from "../../utils/navigation";
import type { GlobalEventProps } from "./GlobalEvent.types";
import {
  useCustomHomePopup,
  useCountryListDialog,
  useCheckInDialog,
  useVipUpDialog,
  useRegisterTurntablePopup,
  useNotLoginNotRechargePopup,
} from "./hooks";
import { useFirstRechargeStagedAutoGrantPopup } from "./hooks/useFirstRechargeStagedAutoGrantPopup";
import { useGroupedRechargeClaimPopup } from "./hooks/useGroupedRechargeClaimPopup";
import { useBalanceInsufficientPopup } from "../../hooks/useBalanceInsufficientPopup";
import { useCashbackBoostPopup } from "../CashbackBoost/useCashbackBoostPopup";

const Event = lazy(() => import("./Event"));

export const GlobalEvent: React.FC<GlobalEventProps> = ({
  showOnPaths,
  hideOnPaths,
  firstRechargeVisible,
  cashbackVisible,
  rechargeOfferVisible,
  redPacketVisible,
  rescueFundVisible,
}) => {
  const [eventVisible] = useGlobalValue("eventVisible", true);
  const { getCurrentPath } = useNavigation();
  const currentPath = getCurrentPath();

  // 初始化所有弹窗 hooks
  useCustomHomePopup();
  useCountryListDialog();
  useCheckInDialog();
  useRegisterTurntablePopup();
  useNotLoginNotRechargePopup();
  useVipUpDialog();
  useBalanceInsufficientPopup();
  /** 首充分批领取弹窗由 useFloatingIcons → useFirstRechargeStagedClaimPopup 注册，避免重复 */
  useFirstRechargeStagedAutoGrantPopup();
  // 充值解锁余额到账弹窗（GROUPED_RECHARGE_TASK.popupItems）；原在 Event.tsx 内调用，
  // 视觉改版 merge a7ebfb67 把该行覆盖丢失，导致充值成功后弹窗不出现。统一移到此注册中心
  useGroupedRechargeClaimPopup();
  useCashbackBoostPopup();

  // 判断是否在指定路径
  const shouldShowOnPath = useMemo(() => {
    if (hideOnPaths && hideOnPaths.length > 0) {
      if (hideOnPaths.includes(currentPath)) return false;
    }

    if (showOnPaths && showOnPaths.length > 0) {
      return showOnPaths.includes(currentPath);
    }

    const allowedPaths = [ROUTES.HOME, ROUTES.HOME_ALT];
    return allowedPaths.includes(currentPath as any);
  }, [currentPath, hideOnPaths, showOnPaths]);

  // 判断是否所有图标都不可见
  const allIconsHidden =
    firstRechargeVisible === false &&
    cashbackVisible === false &&
    rechargeOfferVisible === false &&
    redPacketVisible === false &&
    rescueFundVisible === false;

  // 计算 Event 组件的可见性（只通过 CSS 控制，不销毁组件）
  const eventComponentVisible =
    eventVisible && shouldShowOnPath && !allIconsHidden;

  // 始终渲染 Event 组件以确保 hooks 被初始化（用于全局弹窗队列）
  // 只是通过 visible 属性控制图标显示
  return (
    <Suspense fallback={null}>
      <Event
        visible={eventComponentVisible}
        firstRechargeVisible={firstRechargeVisible}
        cashbackVisible={cashbackVisible}
        rechargeOfferVisible={rechargeOfferVisible}
        redPacketVisible={redPacketVisible}
        rescueFundVisible={rescueFundVisible}
      />
    </Suspense>
  );
};

export default GlobalEvent;
